import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/ServiceNow.css";
import serviceNowLogo from "../../assets/images/partners/ServiceNow.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const platformBento = [
  {
    icon: "⚙️", title: "IT Service Management (ITSM)", colorTop: "linear-gradient(90deg,#00c17b,#0cbf9e)",
    desc: "Modernize IT service delivery with AI-powered incident management, problem management, change management, and self-service portals that reduce ticket volume by 40%+.",
    modules: ["Incident Mgmt", "Problem Mgmt", "Change Mgmt", "Self-Service Portal", "Service Catalog", "SLA Management"],
    wide: false,
  },
  {
    icon: "🏗️", title: "IT Operations Management (ITOM)", colorTop: "linear-gradient(90deg,#0cbf9e,#00b4d8)",
    desc: "Gain full visibility into your hybrid IT environment — from on-premises servers to cloud services — with AI-powered discovery, event management, and service graph mapping.",
    modules: ["Service Graph", "Event Mgmt", "Discovery", "Cloud Mgmt", "AIOps"],
    wide: false,
  },
  {
    icon: "🤝", title: "Customer & Employee Service", colorTop: "linear-gradient(90deg,#00c17b,#34d399)",
    desc: "Deliver consistent, AI-powered service experiences across every channel — from customer portals to employee onboarding workflows, connected across the enterprise.",
    modules: ["CSM", "HR Service Delivery", "Legal Service", "Finance Service"],
    wide: false,
    isStats: true,
    stats: [
      { val: "60%", label: "Faster Onboarding" },
      { val: "45%", label: "Cost Reduction" },
      { val: "89%", label: "Employee Satisfaction" },
      { val: "3×", label: "Faster Resolution" },
    ],
  },
  {
    icon: "🔒", title: "Security Operations & GRC", colorTop: "linear-gradient(90deg,#4ade80,#00c17b)",
    desc: "Close the gap between security and IT operations — automatically prioritizing vulnerabilities, managing risks, and ensuring governance and compliance across your enterprise.",
    modules: ["SecOps", "Vulnerability Response", "Risk Management", "Compliance", "Audit Management"],
    wide: false,
  },
  {
    icon: "📈", title: "Strategic Portfolio Management", colorTop: "linear-gradient(90deg,#00c17b,#059669)",
    desc: "Align technology investments to business outcomes — from project portfolio management and resource optimization to digital transformation governance and OKR tracking.",
    modules: ["SPM", "PPM", "Resource Mgmt", "Innovation Mgmt"],
    wide: false,
  },
];

const itsmCaps = [
  { icon: "🎯", title: "AI-Powered Incident Resolution", desc: "ServiceNow's Now Assist uses generative AI to automatically categorize, route, and suggest resolutions for incidents — reducing mean time to resolution by 35%." },
  { icon: "🔄", title: "Change Management & CAB Automation", desc: "AI risk scoring assesses change risk automatically, reducing CAB meeting time by 70% while maintaining governance and preventing unauthorized changes from impacting production." },
  { icon: "🛒", title: "Self-Service Service Catalog", desc: "Intuitive employee portal with a searchable catalog of IT services — reducing Level 1 ticket volume by 40% through deflection to knowledge base and automated fulfillment." },
  { icon: "📊", title: "Real-Time Performance Analytics", desc: "Executive dashboards with SLA compliance tracking, workload heatmaps, and trend analysis — giving IT leaders actionable visibility into service delivery quality." },
  { icon: "🏗️", title: "Configuration Management Database", desc: "AI-powered CMDB with automated discovery keeping your configuration item data accurate — the foundation for impact analysis, change risk, and service dependency mapping." },
];

const csmMetrics = [
  { val: "40%", label: "Ticket Deflection" },
  { val: "60%", label: "Faster Resolution" },
  { val: "92%", label: "CSAT Score" },
];

const hrCards = [
  { icon: "👋", title: "Employee Onboarding", desc: "Automated onboarding workflows provisioning access, equipment, and training on day one — reducing onboarding time from weeks to days." },
  { icon: "📋", title: "Case & Knowledge Mgmt", desc: "HR case management with knowledge articles enabling employees to self-serve answers to HR, payroll, and benefits questions 24/7." },
  { icon: "🏢", title: "Workplace Service Delivery", desc: "Facilities and workplace management — from desk booking and visitor management to maintenance requests and space optimization." },
  { icon: "📊", title: "HR Analytics & Reporting", desc: "People analytics dashboards tracking turnover risk, engagement scores, and HR service delivery KPIs for strategic workforce decisions." },
];

const aiFeatures = [
  { icon: "🤖", name: "Now Assist — Generative AI", desc: "ServiceNow's native generative AI assistant embedded throughout the platform — drafting responses, summarizing incidents, generating knowledge articles, and writing workflow scripts." },
  { icon: "🔮", name: "Predictive Intelligence", desc: "ML models trained on your ServiceNow data automatically classify incidents, recommend resolutions, and predict SLA breaches before they occur." },
  { icon: "🔬", name: "AIOps Event Correlation", desc: "Machine learning correlates thousands of monitoring alerts into actionable service impairments — reducing alert noise by 95% and surfacing only events that matter." },
  { icon: "⚡", name: "Flow Designer & RPA", desc: "No-code workflow automation with drag-and-drop Flow Designer, plus robotic process automation bots for legacy system integration without API connectivity." },
];

const journeySteps = [
  { icon: "🔍", title: "Platform Assessment", desc: "Evaluate current processes, identify automation opportunities, and define ServiceNow platform scope." },
  { icon: "📐", title: "Architecture & Design", desc: "Design instance architecture, CMDB strategy, integration topology, and custom application roadmap." },
  { icon: "🚀", title: "Core Implementation", desc: "Configure ITSM, CMDB, and self-service portal with ALG Data Guard's certified ServiceNow consultants." },
  { icon: "🔗", title: "Integration & Migration", desc: "Connect ServiceNow to your existing ITSM tools, monitoring platforms, HR systems, and business applications." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "ServiceNow ITSM for clinical infrastructure, HR workflows for credentialing, and patient-facing service portals with HIPAA compliance." },
  { icon: "🏦", name: "Financial Services", desc: "Compliance and risk management with GRC, change management for regulated environments, and SecOps for SOX and PCI compliance tracking." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-authorized ServiceNow for government IT service management, case management, and citizen-facing service delivery portals." },
  { icon: "🏭", name: "Manufacturing", desc: "Operational technology incident management, supplier management workflows, and safety case management integrated with shop-floor systems." },
  { icon: "🎓", name: "Education", desc: "Student and faculty self-service portals, IT support for distributed campuses, and HR workflows for complex academic employment structures." },
  { icon: "🛒", name: "Retail", desc: "Store operations service management, POS incident response automation, and workforce management workflows for seasonal staffing cycles." },
  { icon: "☁️", name: "Technology & SaaS", desc: "DevOps integration with change management, security vulnerability response, and CMDB service mapping for microservices architectures." },
  { icon: "⚡", name: "Energy & Utilities", desc: "OT/IT convergence service management, regulatory compliance tracking, and field service operations on ServiceNow FSM." },
];

const integrations = [
  { icon: "☁️", name: "Microsoft Azure" }, { icon: "🔷", name: "Microsoft 365" }, { icon: "🟠", name: "AWS" },
  { icon: "🟢", name: "Google Cloud" }, { icon: "🔵", name: "Salesforce" }, { icon: "🟡", name: "SAP" },
  { icon: "🔴", name: "Oracle" }, { icon: "🟣", name: "Workday" }, { icon: "⚫", name: "Jira" },
  { icon: "🔵", name: "Splunk" }, { icon: "🟤", name: "PagerDuty" }, { icon: "⚪", name: "Zoom" },
];

const certs = [
  { icon: "🏆", name: "ServiceNow Partner", desc: "Authorized ServiceNow implementation and managed services partner" },
  { icon: "⚙️", name: "ITSM Specialist", desc: "Certified ITSM specialists — Incident, Problem, Change, and CMDB" },
  { icon: "🤖", name: "Now Assist Certified", desc: "ServiceNow AI and Now Assist implementation certified team" },
  { icon: "🔒", name: "SecOps Certified", desc: "Security Operations and GRC implementation specialists" },
];

const faqData = [
  { q: "What is ServiceNow and how does it differ from traditional ITSM tools like Remedy or Jira?", a: "ServiceNow is a cloud-native enterprise service management platform that goes far beyond traditional ITSM tools. While Remedy and Jira are primarily ticketing systems, ServiceNow is a full digital workflow platform that connects IT, HR, customer service, security, legal, and finance operations through a single system of record. ServiceNow's key differentiators include its Now Platform — a low-code application development environment for building custom enterprise workflows — its AI-powered capabilities through Predictive Intelligence and Now Assist, its comprehensive integration ecosystem, and its CMDB-driven service graph that maps relationships between every configuration item in your infrastructure. Unlike legacy ITSM tools that require expensive customization, ServiceNow's out-of-box processes follow ITIL 4 best practices and can be configured without custom code." },
  { q: "What is the ServiceNow CMDB and why does it matter?", a: "The ServiceNow Configuration Management Database (CMDB) is a repository that stores information about all IT components (configuration items, or CIs) in your environment and the relationships between them. A well-maintained CMDB is the foundation of effective IT service management — enabling accurate impact analysis when changes occur, root cause identification during incidents, and service dependency mapping for major incident response. ServiceNow's CMDB uses AI-powered Discovery to automatically populate and keep CI data current through network scanning, agent-based discovery, and integrations with VMware, AWS, Azure, and other infrastructure platforms. ALG Data Guard implements CMDB with a phased approach — starting with high-priority CIs and expanding to full infrastructure coverage." },
  { q: "How long does a ServiceNow ITSM implementation typically take?", a: "A standard ServiceNow ITSM implementation covering Incident Management, Problem Management, Change Management, Service Catalog, and Employee Self-Service typically takes 12–16 weeks from kickoff to go-live. This includes process workshops to configure workflows to your organization's needs, CMDB initial population, integration with Active Directory and monitoring tools, user acceptance testing, and end-user training. More complex implementations adding ITOM Discovery, SecOps, HR Service Delivery, or custom applications extend the timeline accordingly. ALG Data Guard uses an agile delivery approach — deploying core ITSM capabilities first with early user adoption, then layering additional modules in subsequent sprints." },
  { q: "What is Now Assist and how does generative AI improve ServiceNow?", a: "Now Assist is ServiceNow's generative AI suite built natively into the Now Platform. It uses large language models to enhance the ServiceNow experience across several areas: in Agent Workspace, Now Assist summarizes incidents, generates resolution notes, and drafts knowledge articles automatically — reducing documentation time by 60%+. In the self-service portal, Now Assist powers a conversational AI assistant that understands natural language requests and either resolves them automatically or routes them intelligently. In Flow Designer, Now Assist generates workflow scripts and integration logic from natural language prompts. For IT operations, Now Assist correlates and summarizes complex event storms. ALG Data Guard implements Now Assist with proper data governance to ensure AI outputs are accurate and appropriate for your organization." },
  { q: "How does ALG Data Guard support ServiceNow environments after go-live?", a: "ALG Data Guard provides comprehensive ServiceNow managed services including: platform administration and user management, release testing and upgrade validation for ServiceNow's twice-yearly platform releases, performance monitoring and instance health management, workflow and catalog modification requests, CMDB health monitoring and reconciliation, integration maintenance and troubleshooting, custom report and dashboard development, and quarterly business reviews presenting platform adoption metrics and optimization recommendations. We offer tiered managed service agreements from basic administration support to full co-managed partnerships where our team acts as an extension of your ServiceNow team." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function ServiceNow() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(7500, 2500, metricsInView);
  const c2 = useCounter(40, 2000, metricsInView);
  const c3 = useCounter(85, 2000, metricsInView);
  const c4 = useCounter(95, 2000, metricsInView);

  return (
    <div className="sn-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={serviceNowLogo} alt="ServiceNow" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Workflow Automation Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × ServiceNow</h1>
            <p className="pd-identity-desc">
              ServiceNow is the enterprise platform of platforms — connecting IT, HR, security, legal, and customer service through a single cloud-native workflow system. Used by 85% of the Fortune 500, ServiceNow transforms siloed service processes into automated, AI-powered digital workflows. ALG Data Guard is an authorized ServiceNow implementation partner, delivering end-to-end platform deployment, managed services, and continuous optimization.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="sn-hero">
        <div className="sn-hero__mesh" aria-hidden="true" />
        <div className="sn-hero__glow" aria-hidden="true" />
        <div className="sn-hero__inner">
          <div>
            <div className="sn-pill sn-pill--dark"><span className="sn-dot" /> ServiceNow Implementation Partner</div>
            <h2 className="sn-hero__title">
              Make the World of Work <em>Work Better</em> — with ServiceNow
            </h2>
            <p className="sn-hero__body">
              ServiceNow connects every corner of the enterprise through intelligent digital workflows — turning manual, siloed service processes into automated, AI-powered experiences that employees and customers love. ALG Data Guard configures, deploys, and manages your ServiceNow platform from day one to peak maturity.
            </p>
            <div className="sn-hero__actions">
              <button className="sn-btn sn-btn--green" onClick={() => navigate("/contact")}>
                Start My ServiceNow Journey <ArrowIcon />
              </button>
              <button className="sn-btn sn-btn--ghost" onClick={() => navigate("/contact")}>
                Book a Platform Demo
              </button>
            </div>
            <div className="sn-hero__proof">
              <div>
                <div className="sn-hero__proof-val">85%</div>
                <div className="sn-hero__proof-label">Fortune 500 Use ServiceNow</div>
              </div>
              <div>
                <div className="sn-hero__proof-val">7,700+</div>
                <div className="sn-hero__proof-label">Global Customers</div>
              </div>
              <div>
                <div className="sn-hero__proof-val">Partner</div>
                <div className="sn-hero__proof-label">ALG Data Guard Status</div>
              </div>
            </div>
          </div>

          {/* Dashboard Widget Cluster */}
          <div className="sn-dashboard">
            <div className="sn-widget">
              <div className="sn-widget__header">
                <span className="sn-widget__title">Live Service Desk — Today</span>
                <span className="sn-widget__status sn-widget__status--live">● Live</span>
              </div>
              <div className="sn-kpi-row">
                <div className="sn-kpi"><div className="sn-kpi__val sn-kpi__val--green">142</div><div className="sn-kpi__label">Open Incidents</div></div>
                <div className="sn-kpi"><div className="sn-kpi__val">98.4%</div><div className="sn-kpi__label">SLA Compliance</div></div>
                <div className="sn-kpi"><div className="sn-kpi__val sn-kpi__val--yellow">12</div><div className="sn-kpi__label">Awaiting Change</div></div>
              </div>
            </div>
            <div className="sn-widget">
              <div className="sn-widget__header">
                <span className="sn-widget__title">Priority Incidents</span>
                <span className="sn-widget__status sn-widget__status--warn">⚠ 3 P1</span>
              </div>
              <div className="sn-incident-list">
                {[
                  { badge: "P1", badgeCls: "p1", icon: "🔴", text: "Azure AD authentication failure — Prod", time: "2m ago" },
                  { badge: "P2", badgeCls: "p2", icon: "🟡", text: "VPN gateway latency — East Region", time: "18m ago" },
                  { badge: "P3", badgeCls: "p3", icon: "🟢", text: "Email delivery delay — Marketing", time: "1h ago" },
                ].map(inc => (
                  <div key={inc.text} className="sn-incident">
                    <div className={`sn-incident__badge sn-incident__badge--${inc.badgeCls}`}>{inc.icon}</div>
                    <span className="sn-incident__text">{inc.text}</span>
                    <span className="sn-incident__time">{inc.time}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="sn-widget">
              <div className="sn-widget__header">
                <span className="sn-widget__title">Automation Flow — New Employee</span>
              </div>
              <div className="sn-flow">
                {["HR Request ✓", "→", "IT Account ✓", "→", "Equipment ✓", "→", "Training ✓", "→", "Active 🟢"].map((n,i) => (
                  n === "→" ?
                    <span key={i} className="sn-flow-arrow">{n}</span> :
                    <span key={n} className={`sn-flow-node ${n.includes("🟢") ? "sn-flow-node--green" : "sn-flow-node--white"}`}>{n}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Platform Bento ── */}
      <section className="sn-platform">
        <div className="sn-shell">
          <div className="sn-platform__head">
            <div className="sn-pill sn-pill--green"><span className="sn-dot" /> Enterprise Workflow Platform</div>
            <h2 className="sn-title">One Platform. Every Department. Every Workflow.</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>ServiceNow's Now Platform spans every enterprise function — from IT to HR to security to legal — with shared data, shared AI, and shared automation.</p>
          </div>
          <div className="sn-platform-bento">
            {platformBento.map(card => (
              <div key={card.title} className="sn-bento-card">
                <div className="sn-bento-card__color-top" style={{ background: card.colorTop }} />
                <div className="sn-bento-card__inner">
                  <span className="sn-bento-card__icon">{card.icon}</span>
                  <h3 className="sn-bento-card__title">{card.title}</h3>
                  <p className="sn-bento-card__desc">{card.desc}</p>
                  <div className="sn-bento-card__modules">{card.modules.map(m => <span key={m} className="sn-bento-module">{m}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ITSM ── */}
      <section className="sn-itsm">
        <div className="sn-shell">
          <div className="sn-itsm__inner">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
              alt="ServiceNow ITSM dashboard"
              className="sn-itsm__img"
            />
            <div>
              <div className="sn-pill sn-pill--dark"><span className="sn-dot" /> IT Service Management</div>
              <h2 className="sn-itsm__title">ITSM That Runs at Enterprise Speed</h2>
              <p className="sn-itsm__body">ServiceNow ITSM is the most deployed enterprise ITSM platform — used by 60% of large enterprises globally. Its ITIL 4-aligned processes, AI-powered automation, and self-service portal capabilities transform IT service delivery from reactive firefighting to proactive, business-aligned service excellence.</p>
              <div className="sn-itsm__capabilities">
                {itsmCaps.map(cap => (
                  <div key={cap.title} className="sn-cap">
                    <span className="sn-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="sn-cap__title">{cap.title}</div>
                      <p className="sn-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sn-btn sn-btn--green" onClick={() => navigate("/contact")}>
                  Design My ITSM Platform <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Customer Service ── */}
      <section className="sn-csm">
        <div className="sn-shell">
          <div className="sn-csm__inner">
            <div>
              <div className="sn-pill sn-pill--green"><span className="sn-dot" /> Customer Service Management</div>
              <h2 className="sn-csm__title">Customer Service That Proactively Resolves Issues</h2>
              <p className="sn-csm__body">ServiceNow CSM goes beyond traditional CRM — it connects customer-facing service with backend operations, so service agents can see and act on the full context of every customer issue. AI-powered routing, case summarization, and proactive outreach deliver experiences customers remember.</p>
              <p className="sn-csm__body">ALG Data Guard integrates ServiceNow CSM with your existing CRM (Salesforce, Microsoft Dynamics), contact center platform, and backend ERP systems — creating a unified customer service command center.</p>
              <div className="sn-csm__metrics">
                {csmMetrics.map(m => (
                  <div key={m.val} className="sn-csm-metric">
                    <div className="sn-csm-metric__val">{m.val}</div>
                    <div className="sn-csm-metric__label">{m.label}</div>
                  </div>
                ))}
              </div>
              <button className="sn-btn sn-btn--outline" onClick={() => navigate("/contact")}>
                Explore ServiceNow CSM <ArrowIcon />
              </button>
            </div>
            <img
              src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=900&q=80"
              alt="Customer service management"
              className="sn-csm__img"
            />
          </div>
        </div>
      </section>

      {/* ── HR ── */}
      <section className="sn-hr">
        <div className="sn-shell">
          <div className="sn-hr__head">
            <div className="sn-pill sn-pill--green"><span className="sn-dot" /> HR Service Delivery</div>
            <h2 className="sn-title">Transform the Employee Experience from Day One</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>ServiceNow HRSD replaces fragmented HR email chains with structured, trackable, AI-powered employee service delivery across every HR function.</p>
          </div>
          <div className="sn-hr__grid">
            {hrCards.map(card => (
              <div key={card.title} className="sn-hr-card">
                <span className="sn-hr-card__icon">{card.icon}</span>
                <h3 className="sn-hr-card__title">{card.title}</h3>
                <p className="sn-hr-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI ── */}
      <section className="sn-ai">
        <div className="sn-shell">
          <div className="sn-ai__inner">
            <div>
              <div className="sn-pill sn-pill--dark"><span className="sn-dot" /> AI & Automation</div>
              <h2 className="sn-ai__title">AI Built Into Every ServiceNow Workflow</h2>
              <p className="sn-ai__body">ServiceNow is one of the most AI-complete enterprise platforms available — with generative AI through Now Assist, predictive intelligence ML models, process mining, and robotic process automation all built natively into the Now Platform without requiring additional AI vendors.</p>
              <div className="sn-ai__features">
                {aiFeatures.map(f => (
                  <div key={f.name} className="sn-ai-feature">
                    <span className="sn-ai-feature__icon">{f.icon}</span>
                    <div>
                      <div className="sn-ai-feature__name">{f.name}</div>
                      <p className="sn-ai-feature__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sn-btn sn-btn--green" onClick={() => navigate("/contact")}>
                  Explore Now Assist <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="ServiceNow AI automation"
              className="sn-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Transformation Journey ── */}
      <section className="sn-journey">
        <div className="sn-shell">
          <div className="sn-journey__head">
            <div className="sn-pill sn-pill--green"><span className="sn-dot" /> Implementation Journey</div>
            <h2 className="sn-title">ALG Data Guard's ServiceNow Delivery Approach</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Structured delivery from process assessment to production — with agile sprints that deliver value throughout the implementation.</p>
          </div>
          <div className="sn-journey__timeline">
            {journeySteps.map(step => (
              <div key={step.title} className="sn-journey-step">
                <div className="sn-journey-step__icon">{step.icon}</div>
                <h3 className="sn-journey-step__title">{step.title}</h3>
                <p className="sn-journey-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="sn-industries">
        <div className="sn-shell">
          <div className="sn-industries__head">
            <div className="sn-pill sn-pill--green"><span className="sn-dot" /> Industry Solutions</div>
            <h2 className="sn-title">ServiceNow Across Every Industry</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ServiceNow's vertical cloud solutions deliver industry-specific workflows and compliance configurations on the Now Platform.</p>
          </div>
          <div className="sn-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="sn-industry-card">
                <span className="sn-industry-card__icon">{ind.icon}</span>
                <h3 className="sn-industry-card__name">{ind.name}</h3>
                <p className="sn-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integrations ── */}
      <section className="sn-integrations">
        <div className="sn-shell">
          <div className="sn-integrations__head">
            <div className="sn-pill sn-pill--dark"><span className="sn-dot" /> Integration Ecosystem</div>
            <h2 className="sn-title">Connect ServiceNow to Everything</h2>
            <p className="sn-sub">ServiceNow's Integration Hub connects to 900+ enterprise applications — with pre-built spokes for every major cloud, monitoring, identity, and business platform.</p>
          </div>
          <div className="sn-integrations__grid">
            {integrations.map(int => (
              <div key={int.name} className="sn-integration-chip">
                <span className="sn-integration-chip__icon">{int.icon}</span>
                {int.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="sn-metrics" ref={metricsRef}>
        <div className="sn-shell">
          <div className="sn-metrics__inner">
            <div><div className="sn-metric__val">{c1}+</div><div className="sn-metric__label">ServiceNow Customers Globally</div></div>
            <div><div className="sn-metric__val">{c2}%</div><div className="sn-metric__label">Avg Ticket Volume Reduction</div></div>
            <div><div className="sn-metric__val">{c3}%</div><div className="sn-metric__label">Fortune 500 Penetration</div></div>
            <div><div className="sn-metric__val">{c4}%</div><div className="sn-metric__label">Alert Noise Reduction with AIOps</div></div>
          </div>
        </div>
      </section>

      {/* ── Certs ── */}
      <section className="sn-certs">
        <div className="sn-shell">
          <div className="sn-certs__head">
            <div className="sn-pill sn-pill--green"><span className="sn-dot" /> Partner Credentials</div>
            <h2 className="sn-title">Certified ServiceNow Implementation Partner</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's ServiceNow certifications validate our technical depth across the full Now Platform portfolio.</p>
          </div>
          <div className="sn-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="sn-cert-card">
                <span className="sn-cert-card__icon">{c.icon}</span>
                <div className="sn-cert-card__name">{c.name}</div>
                <p className="sn-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="sn-faq">
        <div className="sn-shell">
          <div className="sn-faq__head">
            <div className="sn-pill sn-pill--green" style={{ margin:"0 auto 1rem" }}><span className="sn-dot" /> FAQs</div>
            <h2 className="sn-title">ServiceNow Questions, Answered</h2>
            <p className="sn-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about the ServiceNow platform and ALG Data Guard's implementation and managed services.</p>
          </div>
          <div className="sn-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`sn-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="sn-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="sn-faq-item__q">{faq.q}</span>
                  <span className="sn-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="sn-faq-item__answer"><div className="sn-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sn-cta">
        <div className="sn-cta__inner">
          <div className="sn-pill sn-pill--dark" style={{ margin:"0 auto 1.2rem" }}><span className="sn-dot" /> Get Started</div>
          <h2 className="sn-cta__title">Transform Your Enterprise with ServiceNow</h2>
          <p className="sn-cta__sub">From ITSM modernization and AI-powered automation to full enterprise workflow transformation — ALG Data Guard's certified ServiceNow consultants deliver the platform your employees and customers deserve.</p>
          <div className="sn-cta__actions">
            <button className="sn-btn sn-btn--green" onClick={() => navigate("/contact")}>
              Start My ServiceNow Implementation <ArrowIcon />
            </button>
            <button className="sn-btn sn-btn--ghost" onClick={() => navigate("/contact")}>
              Request a Platform Demo
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
